import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Fade,
  Slide,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Link
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Language as LanguageIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { auth } from '../services/api';

function Login({ onLogin }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(i18n.language || 'fr');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // États pour la modale "Mot de passe oublié"
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    changeLanguage(lang);
  };

  const handleOpenForgotPassword = () => {
    setForgotPasswordEmail(email); // Pré-remplir avec l'email du formulaire de login
    setForgotPasswordMessage('');
    setForgotPasswordError('');
    setForgotPasswordOpen(true);
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordEmail('');
    setForgotPasswordMessage('');
    setForgotPasswordError('');
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordMessage('');

    try {
      const response = await auth.forgotPassword(forgotPasswordEmail);
      setForgotPasswordMessage(response.data.message || t('forgotPasswordSuccess'));
    } catch (err) {
      setForgotPasswordError(err.response?.data?.error || t('forgotPasswordError'));
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await auth.login(email, password);
      onLogin(response.data.token, response.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Slide direction="down" in={true} timeout={800}>
          <Card
            elevation={0}
            sx={{
              backdropFilter: 'blur(20px)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 4,
              overflow: 'visible',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <CardContent sx={{ p: 5 }}>
              {/* Logo et Titre */}
              <Fade in={true} timeout={1000}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <img
                      src={require('../assets/esg-logo.png')}
                      alt="ESG Efficiency Logo"
                      style={{
                        width: '200px',
                        height: 'auto',
                        maxWidth: '100%',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                    }}
                  >
                    {t('login')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Gestion du Patrimoine
                  </Typography>
                </Box>
              </Fade>

              {/* Sélecteur de langue */}
              <Fade in={true} timeout={1200}>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LanguageIcon fontSize="small" />
                      Langue / Language
                    </Box>
                  </InputLabel>
                  <Select
                    value={language}
                    onChange={handleLanguageChange}
                    label="Langue / Language"
                  >
                    <MenuItem value="fr">🇫🇷 Français</MenuItem>
                    <MenuItem value="en">🇬🇧 English</MenuItem>
                    <MenuItem value="pt">🇵🇹 Português</MenuItem>
                    <MenuItem value="mg">🇲🇬 Malagasy</MenuItem>
                  </Select>
                </FormControl>
              </Fade>

              {/* Message d'erreur */}
              {error && (
                <Fade in={Boolean(error)}>
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Fade>
              )}

              {/* Formulaire */}
              <Fade in={true} timeout={1400}>
                <form onSubmit={handleSubmit}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label={t('email')}
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={t('password')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a4190 100%)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                      },
                      '&:disabled': {
                        background: 'rgba(0, 0, 0, 0.12)',
                      },
                    }}
                  >
                    {loading ? 'Connexion...' : t('signIn')}
                  </Button>

                  {/* Lien Mot de passe oublié */}
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={handleOpenForgotPassword}
                      sx={{
                        color: '#667eea',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {t('forgotPassword')}
                    </Link>
                  </Box>
                </form>
              </Fade>

              {/* Footer */}
              <Fade in={true} timeout={1600}>
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    © 2024 Gestion du Patrimoine - v1.0
                  </Typography>
                </Box>
              </Fade>
            </CardContent>
          </Card>
        </Slide>
      </Container>

      {/* Modale Mot de passe oublié */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'visible',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {t('forgotPassword')}
          <IconButton
            onClick={handleCloseForgotPassword}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleForgotPasswordSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('forgotPasswordDescription')}
            </Typography>

            {forgotPasswordMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {forgotPasswordMessage}
              </Alert>
            )}

            {forgotPasswordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {forgotPasswordError}
              </Alert>
            )}

            <TextField
              autoFocus
              required
              fullWidth
              type="email"
              label={t('email')}
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              disabled={forgotPasswordLoading || !!forgotPasswordMessage}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseForgotPassword}
              color="inherit"
            >
              {forgotPasswordMessage ? t('close') : t('cancel')}
            </Button>
            {!forgotPasswordMessage && (
              <Button
                type="submit"
                variant="contained"
                disabled={forgotPasswordLoading || !forgotPasswordEmail}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a4190 100%)',
                  },
                }}
              >
                {forgotPasswordLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('sendNewPassword')
                )}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Login;
